import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { api, SUBJECTS, CLASSES, PREP_OPTIONS, BOARDS, STATES_INDIA, LANGUAGES, TEACHING_MODES } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { GraduationCap, BookOpen, ArrowRight, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function ProfileSetup() {
  const { user, loading, setUser, refresh } = useAuth();
  const navigate = useNavigate();
  const [role] = useState("student");
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/");
    if (user?.role) navigate("/student/dashboard");
  }, [user, loading, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArr = (k, v) => {
    const cur = form[k] || [];
    set(k, cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]);
  };

  const submit = async () => {
    if (!role) return toast.error("Choose whether you're a student or teacher");
    if (!form.phone || !form.city || !form.state) return toast.error("Please add phone, city, and state");
    if (role === "student" && (!form.student_class || !form.board)) return toast.error("Class and board required");
    if (role === "teacher" && (!form.subjects?.length || !form.teaching_classes?.length || !form.teaching_mode)) return toast.error("Subjects, classes and mode required");
    setSaving(true);
    try {
      const r = await api.put("/profile", { role, country: "India", ...form });
      setUser(r.data);
      await refresh();
      sessionStorage.removeItem("luminon_preferred_role");
      toast.success("Profile complete ✨");
      navigate("/student/dashboard");
    } catch { toast.error("Could not save profile"); }
    finally { setSaving(false); }
  };

  if (loading || !user) return null;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 pt-32 pb-16">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass mb-6">
          <Sparkles className="w-3 h-3 text-secondary" />
          <span className="text-[10px] uppercase tracking-widest font-semibold">Almost there</span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Hey {user.name?.split(" ")[0]}.</h1>
        <p className="text-muted-foreground mt-3 text-lg">Tell us who you are so we can tailor Luminon to your journey.</p>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { id: "student", title: "Student", desc: "Browse notes, watch lessons, find tutors.", icon: GraduationCap, accent: "from-cyan-500 to-blue-500" },
            { id: "teacher", title: "Teacher", desc: "Upload content, get discovered, book demos.", icon: BookOpen, accent: "from-indigo-500 to-purple-500" },
          ].map((r) => (
            <button key={r.id} data-testid={`role-${r.id}-btn`}
              className={`text-left p-6 rounded-3xl border-2 transition-all glass ${role === r.id ? "border-primary" : "border-transparent hover:border-primary/40"}`}>
              <div className="flex items-center justify-between">
                <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${r.accent} flex items-center justify-center`}>
                  <r.icon className="w-5 h-5 text-white" strokeWidth={1.8} />
                </div>
                {role === r.id && <Check className="w-5 h-5 text-primary" />}
              </div>
              <div className="font-display text-2xl font-bold mt-4">{r.title}</div>
              <div className="text-sm text-muted-foreground mt-1">{r.desc}</div>
            </button>
          ))}
        </div>

        {role && (
          <div className="mt-10 glass rounded-3xl p-8 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label>Phone</Label>
                <Input data-testid="input-phone" value={form.phone || ""} onChange={(e) => set("phone", e.target.value)}
                  placeholder="+91 98xxxxxxxx" className="h-12 rounded-xl mt-1" />
              </div>
              <div>
                <Label>City</Label>
                <Input data-testid="input-city" value={form.city || ""} onChange={(e) => set("city", e.target.value)} className="h-12 rounded-xl mt-1" />
              </div>
              <div>
                <Label>State</Label>
                <Select value={form.state} onValueChange={(v) => set("state", v)}>
                  <SelectTrigger className="h-12 rounded-xl mt-1" data-testid="select-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent>{STATES_INDIA.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {role === "student" ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div>
                  <Label>Class</Label>
                  <Select value={form.student_class} onValueChange={(v) => set("student_class", v)}>
                    <SelectTrigger className="h-12 rounded-xl mt-1" data-testid="select-class"><SelectValue placeholder="Class" /></SelectTrigger>
                    <SelectContent>{CLASSES.map(c => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Board</Label>
                  <Select value={form.board} onValueChange={(v) => set("board", v)}>
                    <SelectTrigger className="h-12 rounded-xl mt-1" data-testid="select-board"><SelectValue placeholder="Board" /></SelectTrigger>
                    <SelectContent>{BOARDS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Preparing for</Label>
                  <Select value={form.prep_for} onValueChange={(v) => set("prep_for", v)}>
                    <SelectTrigger className="h-12 rounded-xl mt-1" data-testid="select-prep"><SelectValue placeholder="Any" /></SelectTrigger>
                    <SelectContent>{PREP_OPTIONS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label>Qualifications</Label>
                    <Input data-testid="input-qual" value={form.qualifications || ""} onChange={(e) => set("qualifications", e.target.value)}
                      placeholder="M.Sc, B.Ed" className="h-12 rounded-xl mt-1" />
                  </div>
                  <div>
                    <Label>Experience (yrs)</Label>
                    <Input data-testid="input-exp" type="number" value={form.experience_years || ""}
                      onChange={(e) => set("experience_years", parseInt(e.target.value) || 0)} className="h-12 rounded-xl mt-1" />
                  </div>
                  <div>
                    <Label>Hourly rate (₹)</Label>
                    <Input data-testid="input-rate" type="number" value={form.hourly_rate || ""}
                      onChange={(e) => set("hourly_rate", parseInt(e.target.value) || 0)} className="h-12 rounded-xl mt-1" />
                  </div>
                  <div>
                    <Label>Teaching mode</Label>
                    <Select value={form.teaching_mode} onValueChange={(v) => set("teaching_mode", v)}>
                      <SelectTrigger className="h-12 rounded-xl mt-1" data-testid="select-mode"><SelectValue placeholder="Mode" /></SelectTrigger>
                      <SelectContent>{TEACHING_MODES.map(m => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Bio</Label>
                  <Textarea data-testid="input-bio" value={form.bio || ""} onChange={(e) => set("bio", e.target.value)}
                    placeholder="Tell students about your teaching approach." className="rounded-xl mt-1" rows={3} />
                </div>
                <div>
                  <Label>Certifications</Label>
                  <Input data-testid="input-cert" value={form.certifications || ""}
                    onChange={(e) => set("certifications", e.target.value)} placeholder="e.g. CBSE Master Trainer, Cambridge PGCE"
                    className="h-12 rounded-xl mt-1" />
                </div>
                <div>
                  <Label>Availability</Label>
                  <Input data-testid="input-avail" value={form.availability || ""} onChange={(e) => set("availability", e.target.value)}
                    placeholder="Mon-Fri, 4pm-8pm IST" className="h-12 rounded-xl mt-1" />
                </div>
                <div>
                  <Label>Subjects you teach</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {SUBJECTS.map(s => (
                      <button key={s} type="button" onClick={() => toggleArr("subjects", s)} data-testid={`subject-chip-${s}`}
                        className={`px-4 py-2 rounded-full text-sm border transition-all ${form.subjects?.includes(s) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/40"}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Languages</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {LANGUAGES.map(l => (
                      <button key={l} type="button" onClick={() => toggleArr("languages", l)} data-testid={`lang-chip-${l}`}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-all ${form.languages?.includes(l) ? "bg-secondary text-secondary-foreground border-secondary" : "border-border hover:border-secondary/40"}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Classes you teach</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {CLASSES.map(c => (
                      <button key={c} type="button" onClick={() => toggleArr("teaching_classes", c)} data-testid={`class-chip-${c}`}
                        className={`w-11 h-11 rounded-full text-sm border transition-all ${form.teaching_classes?.includes(c) ? "bg-accent text-accent-foreground border-accent" : "border-border hover:border-accent/40"}`}>
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            <Button onClick={submit} disabled={saving} data-testid="submit-profile-btn"
              className="w-full h-14 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground text-base">
              {saving ? "Saving…" : "Complete profile"}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
